// src/services/platformFeedbackService.js

import { repo } from "../dbservice/repo";
import { PlatformFeedbackModel } from "../models/PlatformFeedbackModel";

// ── Named exports (backwards-compatible with existing imports) ────────────────
// Feedbackform.jsx      → imports { createFeedback }
// PlatformFeedback.jsx  → imports { fetchAllFeedback, updateFeedbackStatus,
//                                   deleteFeedback, bulkUpdateStatus,
//                                   bulkDeleteFeedback }
export const fetchAllFeedback      = (...args) => platformFeedbackService.fetchAllFeedback(...args);
export const createFeedback        = (...args) => platformFeedbackService.createFeedback(...args);
export const updateFeedbackStatus  = (...args) => platformFeedbackService.updateFeedbackStatus(...args);
export const deleteFeedback        = (...args) => platformFeedbackService.deleteFeedback(...args);
export const bulkUpdateStatus      = (...args) => platformFeedbackService.bulkUpdateStatus(...args);
export const bulkDeleteFeedback    = (...args) => platformFeedbackService.bulkDeleteFeedback(...args);
class PlatformFeedbackService {
  constructor() {
    this.collectionName = "platformFeedback";
  }

 get currentUser() {
  if (typeof window === "undefined") return null;
  try {
    const data = localStorage.getItem("userData");
    if (!data) return null;
    const parsed = JSON.parse(data);
  
    return parsed?.user ?? parsed?.data ?? parsed;
  } catch (e) {
    console.error("[PlatformFeedbackService] Failed to parse userData", e);
    return null;
  }
}

  // ── Core persistence (mirrors CategoryService.upsertData) ──────────────────

  async upsertData(targetId, data, { isCreation = false } = {}) {
    if (!targetId) throw new Error("Target Feedback ID is required");

    let finalData = { ...data };

    if (!isCreation) {
      const existing = await this.fetchById(targetId);
      if (existing) {
        finalData = {
          ...existing,
          ...data,
          metadata: {
            ...(existing.metadata || {}),
            ...(data.metadata || {}),
            createdAt:
              existing.metadata?.createdAt ||
              existing.createdAt ||
              new Date().toISOString(),
            createdBy:
              existing.metadata?.createdBy || existing.createdBy || "system",
          },
        };
      }
    }

    const instance = new PlatformFeedbackModel(
      { ...finalData, id: targetId, feedbackId: data.feedbackId || targetId },
      { isCreation }
    );

    // Skip strict validation on partial updates so bulk status changes
    // and delete flows don't fail on legacy rows missing rating/userId.
   if (isCreation && !instance.isValid()) {
  const missing = [];
  if (!instance.userId || !String(instance.userId).trim()) missing.push("userId");
  if (!instance.userEmail || !String(instance.userEmail).trim()) missing.push("userEmail");
  if (instance.rating == null || !Number.isInteger(Number(instance.rating)) || Number(instance.rating) < 1 || Number(instance.rating) > 5) {
    missing.push(`rating(1-5, got ${instance.rating})`);
  }
  if (instance.category && !PlatformFeedbackModel.CATEGORY_VALUES.includes(instance.category)) {
    missing.push(`category(got ${instance.category})`);
  }
  if (instance.status && !PlatformFeedbackModel.STATUS_VALUES.includes(instance.status)) {
    missing.push(`status(got ${instance.status})`);
  }
  throw new Error(`Invalid feedback payload: ${missing.join(", ")}`);
}

    const payload = instance.toPayload();
    const nowIso = new Date().toISOString();

    payload.metadata = {
      ...(payload.metadata || {}),
      updatedAt: nowIso,
      updatedBy:
        data.metadata?.updatedBy || payload.metadata?.updatedBy || "system",
    };

    await repo.upsertData(this.collectionName, targetId, payload);
    return { success: true, id: targetId };
  }

  async fetchById(id) {
    if (!id) {
      console.error("[PlatformFeedbackService] fetchById: ID missing");
      return null;
    }
    try {
      return (await repo.fetchById(this.collectionName, id)) || null;
    } catch (error) {
      console.error(`[PlatformFeedbackService] Failed to fetch feedback ${id}:`, error);
      return null;
    }
  }

  // ── Public API (kept identical to the original module exports) ─────────────

  /**
   * Fetch all platform feedback.
   * Signature unchanged — no args, returns array sorted newest-first.
   */
  async fetchAllFeedback() {
    return await repo.fetchData(
      this.collectionName,
      [],
      [{ field: "timestamp", order: "desc" }],
      null,
    );
  }

  /**
   * Create feedback.
   * Delegates to upsertData({ isCreation: true }) so the model stamps
   * metadata + timestamp and validates the payload.
   */
  async createFeedback(feedbackData, currentUser = {}) {
    const user = currentUser?.email ? currentUser : this.currentUser;
    const targetId =
      feedbackData.id || feedbackData.feedbackId ||
      `feedback_${Date.now()}_${Math.random().toString(36).slice(2)}`;

    const payload = {
      ...feedbackData,
      id: targetId,
      feedbackId: feedbackData.feedbackId || targetId,
     userId:    feedbackData.userId    || user?.uid || user?.id || null,
userEmail: feedbackData.userEmail || feedbackData.email || user?.email || null,
userName:  feedbackData.userName  || user?.displayName || user?.name || user?.email || null,
      companyId: feedbackData.companyId || user?.companyId || null,
      company:   feedbackData.company   || user?.company   || null,
      status:    feedbackData.status    || "active",
      metadata: {
        ...(feedbackData.metadata || {}),
        createdBy: user?.email || feedbackData.userEmail || "system",
        updatedBy: user?.email || feedbackData.userEmail || "system",
      },
    };

    return await this.upsertData(targetId, payload, { isCreation: true });
  }

  /**
   * Update feedback status.
   * Signature unchanged: (feedbackId, newStatus).
   */
  async updateFeedbackStatus(feedbackId, newStatus) {
    return await this.upsertData(
      feedbackId,
      { status: newStatus },
      { isCreation: false },
    );
  }

  /**
   * Delete feedback.
   */
  async deleteFeedback(feedbackId) {
    if (!feedbackId) throw new Error("Feedback ID is required for deletion.");
    try {
      await repo.deleteData(this.collectionName, feedbackId);
      return { success: true };
    } catch (error) {
      console.error(`[PlatformFeedbackService] Failed to delete feedback ${feedbackId}:`, error);
      throw error;
    }
  }

  /**
   * Bulk update status.
   * Signature unchanged: (feedbackIds, newStatus).
   */
  async bulkUpdateStatus(feedbackIds, newStatus) {
    const promises = feedbackIds.map((id) =>
      this.updateFeedbackStatus(id, newStatus)
    );
    return await Promise.all(promises);
  }

  /**
   * Bulk delete.
   */
  async bulkDeleteFeedback(feedbackIds) {
    const promises = feedbackIds.map((id) => this.deleteFeedback(id));
    return await Promise.all(promises);
  }
}

// Single shared instance
export const platformFeedbackService = new PlatformFeedbackService();
